import type {
  ExplorationStatus,
  SourcingExplorationSummary,
  SourcingFlowKind,
} from "@shared/labs/sourcing";
import { History, Loader2, RotateCcw, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { rpcClient } from "@/lib/rpc";

const STATUS_LABEL: Record<ExplorationStatus, string> = {
  pending: "na fila",
  running: "explorando",
  awaiting_input: "aguardando avaliação",
  harvesting: "colhendo",
  done: "concluída",
  error: "erro",
};

const isActive = (s: ExplorationStatus) =>
  s === "pending" || s === "running" || s === "awaiting_input" || s === "harvesting";

/**
 * The caller's saved runs for ONE flow (`kind`). Reopening a finished one replays
 * its persisted shortlist for free (no new Apify spend) — the whole point of
 * saving them, since a run costs real money. Renders nothing while loading or with
 * no history.
 */
export function RecentExplorations({
  onReopen,
  kind = "explore",
}: {
  onReopen: (id: string) => void;
  /** Which flow's history to list — keeps Explore and Busca precisa separate. */
  kind?: SourcingFlowKind;
}) {
  const [items, setItems] = useState<SourcingExplorationSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    rpcClient.labs
      .sourcingExplorationList({ limit: 8, kind })
      .then((d) => {
        if (!cancelled) setItems(Array.isArray(d) ? d : []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  if (loading || items.length === 0) return null;

  return (
    <section className="mt-10 space-y-3 border-t border-border pt-7">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          <History size={12} strokeWidth={1.75} />{" "}
          {kind === "precise" ? "Buscas salvas" : "Explorações salvas"}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-[3px] font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          reabrir sem custo
        </span>
      </div>

      <ul className="space-y-2">
        {items.map((it) => {
          const active = isActive(it.status);
          return (
            <li key={it.id}>
              <button
                type="button"
                onClick={() => onReopen(it.id)}
                className="group flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 text-left transition-colors hover:border-ring"
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-border bg-muted/40 text-muted-foreground">
                  {active ? (
                    <Loader2 size={13} strokeWidth={1.75} className="animate-spin" />
                  ) : (
                    <RotateCcw size={13} strokeWidth={1.75} />
                  )}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-foreground">{it.brief}</span>
                  <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[10.5px] text-muted-foreground">
                    <span className="uppercase tracking-[0.12em]">{STATUS_LABEL[it.status]}</span>
                    {it.status === "done" && (
                      <span className="inline-flex items-center gap-1">
                        <Users size={11} /> <span className="tabular-nums">{it.resultCount}</span>{" "}
                        candidatos
                      </span>
                    )}
                    <span className="tabular-nums">{it.pagesUsed} páginas</span>
                  </span>
                </span>

                <span className="shrink-0 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[11px] font-medium tabular-nums text-muted-foreground">
                  ${it.usd.toFixed(2)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
